import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/cartContext';
import { ThemeProvider } from './context/ThemeContext';
import LoadingScreen from './components/LoadingScreen';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingCTA from './components/FloatingCTA';
import SmartAssistant from './components/SmartAssistant';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// After a deploy, a stale index.html may ask for a route chunk whose hashed
// name is gone → ChunkLoadError. Retry once via a hard reload (fresh chunk
// names), guarded so it can never loop; a second failure surfaces to
// ErrorBoundary. This kills the "page never finishes loading" class of bug.
const RELOAD_KEY = 'mbegu_chunk_reloaded';
function lazyRetry<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      const mod = await factory();
      try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* ignore */ }
      return mod;
    } catch (err) {
      let reloaded = false;
      try { reloaded = sessionStorage.getItem(RELOAD_KEY) === '1'; } catch { /* ignore */ }
      if (!reloaded) {
        try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch { /* ignore */ }
        window.location.reload();
        return new Promise<{ default: T }>(() => { /* hold render until reload */ });
      }
      throw err;
    }
  });
}

const NotFound = lazyRetry(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));

// Lazy-load the page components
const Home = lazyRetry(() => import('./pages/Home'));
const AdminPage = lazyRetry(() => import('./pages/AdminPage'));
const Wallpapers = lazyRetry(() => import('./pages/Wallpapers'));
const Products = lazyRetry(() => import('./pages/Products'));
const SeedlingWizard = lazyRetry(() => import('./pages/SeedlingWizard'));
const Grow = lazyRetry(() => import('./pages/Grow'));
const GrowCounty = lazyRetry(() => import('./pages/GrowCounty'));
const GrowCrop = lazyRetry(() => import('./pages/GrowCrop'));
const SeedlingCalculator = lazyRetry(() => import('./pages/SeedlingCalculator'));
const Weather = lazyRetry(() => import('./pages/Weather'));
const Compare = lazyRetry(() => import('./pages/Compare'));
const ExploreMap = lazyRetry(() => import('./pages/ExploreMap'));
const HowWeGrow = lazyRetry(() => import('./pages/HowWeGrow'));
const About = lazyRetry(() => import('./pages/About'));
const Contact = lazyRetry(() => import('./pages/Contact'));
const Dashboard = lazyRetry(() => import('./pages/DashboardPage'));
const Login = lazyRetry(() => import('./pages/auth/Login'));
const Register = lazyRetry(() => import('./pages/auth/Register'));
const Wallet = lazyRetry(() => import('./pages/Wallet'));
const Cart = lazyRetry(() => import('./pages/Cart'));
const Checkout = lazyRetry(() => import('./pages/Checkout'));
const OrderConfirmation = lazyRetry(() => import('./pages/OrderConfirmation'));
const Orders = lazyRetry(() => import('./pages/Orders'));
const VerifyEmail = lazyRetry(() => import('./pages/auth/VerifyEmail'));
const CompanyProfile = lazyRetry(() => import('./pages/CompanyProfile'));
const BulkRestoration = lazyRetry(() => import('./pages/BulkRestoration'));
const SponsorTree     = lazyRetry(() => import('./pages/SponsorTree'));
const CorporateESG    = lazyRetry(() => import('./pages/CorporateESG'));
const ShambGame       = lazyRetry(() => import('./pages/ShambGame'));
const ProductDetail = lazyRetry(() => import('./pages/ProductDetail'));
const Gallery = lazyRetry(() => import('./pages/Gallery'));
const TrackYourTrees    = lazyRetry(() => import('./pages/TrackYourTrees'));
const RestorationTrack  = lazyRetry(() => import('./pages/RestorationTrack'));
const FieldCapture      = lazyRetry(() => import('./pages/FieldCapture'));
const Impact            = lazyRetry(() => import('./pages/Impact'));
const FreshProduce      = lazyRetry(() => import('./pages/FreshProduce'));
const InvoiceManager    = lazyRetry(() => import('./pages/InvoiceManager'));

const App: React.FC = () => {
  // ── Lenis smooth scroll ────────────────────────────────────────────────────
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let lenis: any;
    let rafId: number;
    // Dynamic import so it tree-shakes on builds that don't need it
    import('lenis').then(({ default: Lenis }) => {
      lenis = new Lenis({
        duration: 1.1,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      const raf = (time: number) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
      rafId = requestAnimationFrame(raf);
    }).catch(() => {}); // silently skip if package not installed yet
    return () => {
      if (lenis) lenis.destroy();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <ThemeProvider>
          <BrowserRouter>
            <Navbar />
            <FloatingCTA />
            <SmartAssistant />
            <ErrorBoundary>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                {/* CRITICAL FIX: Tell React Router to ignore Firebase Auth iframe paths */}
                <Route path="/__/auth/*" element={null} />

                <Route path="/" element={<Home />} />
                <Route path="/find-seedlings" element={<SeedlingWizard />} />
                <Route path="/grow" element={<Grow />} />
                <Route path="/grow/county/:slug" element={<GrowCounty />} />
                <Route path="/grow/crop/:slug" element={<GrowCrop />} />
                <Route path="/calculator" element={<SeedlingCalculator />} />
                <Route path="/weather" element={<Weather />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/explore" element={<ExploreMap />} />
                <Route path="/how-we-grow" element={<HowWeGrow />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:slug" element={<ProductDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-confirmation/:orderId" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                <Route path="/company-profile" element={<CompanyProfile />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/track"              element={<ProtectedRoute><TrackYourTrees /></ProtectedRoute>} />
                <Route path="/restoration-track" element={<ProtectedRoute><RestorationTrack /></ProtectedRoute>} />
                <Route path="/field-capture"     element={<ProtectedRoute><FieldCapture /></ProtectedRoute>} />
                <Route path="/impact" element={<Impact />} />
                <Route path="/fresh-produce"    element={<FreshProduce />} />
                <Route path="/invoices"         element={<ProtectedRoute><InvoiceManager /></ProtectedRoute>} />
                <Route path="/bulk-restoration" element={<BulkRestoration />} />
                <Route path="/sponsor-a-tree"  element={<SponsorTree />} />
                <Route path="/corporate-esg"   element={<CorporateESG />} />
                <Route path="/play"            element={<ShambGame />} />
                <Route path="/wallpapers" element={<Wallpapers />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
            <Footer />
          </BrowserRouter>
        </ThemeProvider>
      </CartProvider>
    </AuthProvider>
  );
};

export default App;
